import { TaskType } from '@/shared/domain/taskTypes';
import { data_from_server_post } from '@/shared/http/client';
import {
    applyAiGeneratedColumnDefaults,
    defaultResponseColumnForTask,
} from '@/features/datasets/utils/aiGeneratedDatasetDefaults';

/**
 * Resolve columns + default column mappings for a library dataset/benchmark
 * option (same rules as the legacy FromLibraryTab mapping modal).
 */
export function resolveLibraryPickDefaults(optionMeta, taskType) {
    const colMappings = optionMeta?.column_mappings;
    const extraKeys = optionMeta?.extra_params_keys || [];
    const fileColumns = optionMeta?.file_column_names || [];

    let allColumns;
    if (fileColumns.length > 0) {
        allColumns = [...fileColumns];
    } else {
        const colSet = new Set();
        if (colMappings?.example_column_name) colSet.add(colMappings.example_column_name);
        if (colMappings?.label_column_name) colSet.add(colMappings.label_column_name);
        if (colMappings?.relations_column_name) colSet.add(colMappings.relations_column_name);
        extraKeys.forEach((key) => colSet.add(key));
        allColumns = [...colSet];
    }

    const sourceTaskType = optionMeta?.task_type;
    const sameTaskType = Boolean(sourceTaskType && sourceTaskType === taskType);
    const isClassificationTask = taskType === TaskType.classification;
    const hasClassificationLabels =
        isClassificationTask || sourceTaskType === TaskType.classification;

    const aiApplied = applyAiGeneratedColumnDefaults({
        columns: allColumns,
        mappings: colMappings || {},
        taskType,
        dataCreationType: optionMeta?.data_creation_type,
        datasetName: optionMeta?.value,
    });
    allColumns = aiApplied.columns;

    const columnNamesSet = new Set(
        allColumns
            .map((col) => (typeof col === 'object' && col !== null ? col.value || col.label : col))
            .filter(Boolean)
    );
    if (hasClassificationLabels && !columnNamesSet.has('labels') && !columnNamesSet.has('label')) {
        columnNamesSet.add('labels');
        allColumns = [...allColumns, 'labels'];
    }
    const columnNames = [...columnNamesSet];
    const firstAvailableColumn = (candidates) =>
        candidates.find((candidate) => columnNames.includes(candidate));

    const responseColumn = defaultResponseColumnForTask(taskType);
    const columnMappings = {
        example_column_name:
            aiApplied.mappings.example_column_name || firstAvailableColumn(['example', 'text']),
        label_column_name: isClassificationTask
            ? (sameTaskType ? colMappings?.label_column_name : undefined) ||
              firstAvailableColumn(['labels', 'label'])
            : (sameTaskType ? colMappings?.label_column_name : undefined) ||
              firstAvailableColumn([responseColumn]),
        relations_column_name: sameTaskType ? colMappings?.relations_column_name : undefined,
        classification_labels_delimiter: sameTaskType
            ? colMappings?.classification_labels_delimiter
            : undefined,
        label_mappings: sameTaskType ? colMappings?.label_mappings : undefined,
        relations_mappings: sameTaskType ? colMappings?.relations_mappings : undefined,
    };

    return {
        datasetName: optionMeta?.value,
        columns: allColumns,
        columnMappings,
    };
}

/** How many library records the wizard fetches for Review Examples shuffle. */
export const LIBRARY_PREVIEW_SAMPLE_LIMIT = 20;

/**
 * Fetch sample record(s) (and optional distinct labels) for a library dataset.
 * `has_empty_label_values` reports whether the label/output column holds empty
 * values anywhere in the (already fully ingested) dataset — null = unverifiable.
 * Pass `limit` > 1 so Review Examples shuffle has more than one preview value.
 */
export function fetchLibraryDatasetSample({
    clientId,
    datasetName,
    isBenchmark = false,
    labelColumn = null,
    distinctTarget = null,
    limit = 1,
}) {
    return new Promise((resolve, reject) => {
        if (!clientId || !datasetName) {
            resolve({
                sample: {},
                samples: [],
                distinct_labels: [],
                has_empty_label_values: null,
            });
            return;
        }
        const payload = {
            client_id: clientId,
            dataset_name: datasetName,
            is_benchmark: isBenchmark,
        };
        if (labelColumn) payload.label_column = labelColumn;
        if (distinctTarget) payload.distinct_target = distinctTarget;
        if (limit && limit > 1) payload.limit = limit;
        data_from_server_post(
            'dataset-sample-record',
            payload,
            (response) => {
                const sample = response?.sample || {};
                const samples = Array.isArray(response?.samples) && response.samples.length
                    ? response.samples
                    : Object.keys(sample).length
                      ? [sample]
                      : [];
                resolve({
                    sample,
                    samples,
                    distinct_labels: response?.distinct_labels || [],
                    distinct_relations: response?.distinct_relations || [],
                    has_empty_label_values: response?.has_empty_label_values ?? null,
                });
            },
            (error) => reject(new Error(error || 'Failed to load library dataset sample'))
        );
    });
}

/**
 * Whether a library dataset has empty values in `labelColumn` (null = unverifiable).
 * Used to re-check when the user picks a different labels column after a library pick.
 */
export async function fetchLibraryDatasetHasEmptyLabels({
    clientId,
    datasetName,
    isBenchmark = false,
    labelColumn,
}) {
    const { has_empty_label_values: hasEmpty } = await fetchLibraryDatasetSample({
        clientId,
        datasetName,
        isBenchmark,
        labelColumn,
    });
    return hasEmpty;
}
